import { User } from '../types/user';
import { api } from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials extends LoginCredentials {
  name: string;
  phone?: string;
}

export interface AuthResponse {
  user: User;
  access_token: string;
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
}

const storeTokens = (access_token: string, refresh_token: string) => {
  // Ensure tokens are trimmed before storage
  localStorage.setItem('access_token', access_token.trim());
  if (refresh_token) {
    localStorage.setItem('refresh_token', refresh_token.trim());
  }
};

const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

export const login = async (credentials: LoginCredentials): Promise<User> => {
  const data: AuthResponse = await api.post('/auth/login', credentials);
  storeTokens(data.access_token, data.refresh_token);
  return data.user;
};

export const signup = async (credentials: SignupCredentials): Promise<User> => {
  const data: AuthResponse = await api.post('/auth/signup', credentials);
  storeTokens(data.access_token, data.refresh_token);
  return data.user;
};

export const logout = async (): Promise<void> => {
  try {
    await api.post('/auth/logout', {});
  } finally {
    clearTokens();
  }
};

export const refreshToken = async (): Promise<RefreshTokenResponse> => {
  const refresh_token = localStorage.getItem('refresh_token');
  if (!refresh_token) {
    throw new Error('No refresh token available');
  }
  try {
    const data = await api.post<RefreshTokenResponse>('/auth/refresh', { refresh_token });
    if (!data.access_token) {
      throw new Error('Invalid refresh token response');
    }
    storeTokens(data.access_token, '');
    return data;
  } catch (error) {
    clearTokens();
    throw error;
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  const access_token = localStorage.getItem('access_token');
  if (!access_token) return null;

  try {
    return await api.get<User>('/auth/me');
  } catch (error) {
    if (error instanceof Error && error.message.includes('401')) {
      try {
        await refreshToken();
        return await api.get<User>('/auth/me');
      } catch (refreshError) {
        clearTokens();
        return null;
      }
    }
    return null;
  }
}; 